import type { DocumentViewer, Document, SelectionCoords } from '@/interfaces/document-viewer'
import type { TextSelection } from '@/types'
import { logger } from '@/utils/logger'

/**
 * Handles image viewing functionality
 * Extracted from the monolithic ModalManager class
 */
export class ImageViewer implements DocumentViewer {
  private container: HTMLElement
  private apiService: any
  private currentFile: any = null
  private scale: number = 1.0
  private initialScale: number = 1.0 // Store the initial fitted scale
  private selections: TextSelection[] = []
  private imageElement: HTMLImageElement | null = null
  private selectionOverlay: HTMLDivElement | null = null
  
  // Selection state
  private isSelecting: boolean = false
  private selectionStart: { x: number; y: number } | null = null
  private currentSelection: HTMLDivElement | null = null
  
  constructor(container: HTMLElement, apiService: any) {
    this.container = container
    this.apiService = apiService
    this.init()
  }
  
  private init(): void {
    this.setupEventListeners()
    logger.debug('ImageViewer initialized')
  }
  
  /**
   * Load a document (implements DocumentViewer interface)
   */
  async loadDocument(document: Document): Promise<void> {
    this.currentFile = document
    await this.loadImage(document)
  }
  
  /**
   * Load an image file
   */
  async loadImage(imageFile: any): Promise<void> {
    try {
      logger.debug('Loading image:', imageFile.filename)
      
      this.currentFile = imageFile
      this.scale = 1.0
      this.selections = []
      
      // Clear existing content
      this.container.innerHTML = ''
      
      // Create image viewer HTML
      this.container.innerHTML = `
        <div class="image-viewer-container" style="height: 100%; display: flex; flex-direction: column;">
          <div class="image-viewer-controls" style="padding: 10px; border-bottom: 1px solid #e5e7eb; background: #f9fafb;">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-4">
                <button id="image-zoom-in" class="btn btn-sm btn-outline">🔍+</button>
                <button id="image-zoom-out" class="btn btn-sm btn-outline">🔍-</button>
                <button id="image-reset-zoom" class="btn btn-sm btn-outline">Reset</button>
                <span id="image-zoom-level" class="text-sm text-gray-600">100%</span>
              </div>
              <div class="text-sm text-gray-600">
                🖼️ ${imageFile.original_filename || imageFile.filename}
              </div>
            </div>
          </div>
          <div class="image-viewer-content" style="flex: 1; overflow: auto; position: relative; background: #f3f4f6;">
            <div class="image-container" style="position: relative; display: inline-block; min-width: 100%; min-height: 100%;">
              <img 
                id="image-display" 
                style="display: block; max-width: none; cursor: crosshair; user-select: none;"
                alt="Document image"
              />
              <div id="image-selection-overlay" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none;"></div>
            </div>
          </div>
        </div>
      `
      
      // Get references to elements
      this.imageElement = this.container.querySelector('#image-display') as HTMLImageElement
      this.selectionOverlay = this.container.querySelector('#image-selection-overlay') as HTMLDivElement
      
      // Load the image
      await this.loadImageInViewer(imageFile)
      
      // CRITICAL FIX: Setup event listeners after HTML is created
      this.setupEventListeners()
      
      // Setup selection event listeners
      this.setupSelectionEventListeners()
      
      logger.debug('Image loaded successfully')
      
    } catch (error) {
      logger.error('Failed to load image:', error)
      throw error
    }
  }
  
  /**
   * Load image in viewer
   */
  private async loadImageInViewer(imageFile: any): Promise<void> {
    if (!this.imageElement) return

    try {
      logger.debug('🖼️ Loading image using authenticated API service')
      
      // Use authenticated API service to download image as blob
      const imageBlob = await this.apiService.downloadDocumentAsBlob(imageFile.id)
      const objectUrl = URL.createObjectURL(imageBlob)
      
      // Load image with proper error handling
      this.imageElement.onload = () => {
        logger.debug('✅ Image loaded successfully')
        // CRITICAL FIX: Add delay to ensure image is fully rendered before fitting
        setTimeout(() => {
          this.fitImageToContainer()
          logger.debug('🖼️ Image fitted to container after load')
        }, 100)
        // Clean up object URL after loading
        URL.revokeObjectURL(objectUrl)
      }
      
      this.imageElement.onerror = () => {
        logger.error('❌ Failed to load image')
        URL.revokeObjectURL(objectUrl) // Clean up on error
        this.imageElement!.style.display = 'none'
        this.container.innerHTML = `
          <div class="flex items-center justify-center h-full bg-gray-50 rounded-lg">
            <div class="text-center p-8">
              <div class="text-6xl mb-4">❌</div>
              <h3 class="text-lg font-semibold text-gray-900 mb-2">Failed to Load Image</h3>
              <p class="text-gray-600">Could not load the image document.</p>
            </div>
          </div>
        `
      }
      
      this.imageElement.src = objectUrl
      
    } catch (error) {
      logger.error('Failed to load image:', error)
      throw error
    }
  }
  
  /**
   * Fit image to container
   */
  private fitImageToContainer(): void {
    if (!this.imageElement) return
    
    const container = this.imageElement.parentElement
    if (!container) return
    
    const containerWidth = container.clientWidth - 40 // Account for padding
    const containerHeight = container.clientHeight - 40
    const imageWidth = this.imageElement.naturalWidth
    const imageHeight = this.imageElement.naturalHeight
    
    // CRITICAL FIX: Fit to width by default, not limited to 100%
    const scaleX = containerWidth / imageWidth
    const scaleY = containerHeight / imageHeight
    const scale = Math.min(scaleX, scaleY) // Remove the 1.0 limit to allow fitting to width
    
      this.scale = scale
      this.initialScale = scale // Store the initial fitted scale
      this.imageElement.style.width = `${imageWidth * scale}px`
      this.imageElement.style.height = `${imageHeight * scale}px`
      
      this.updateZoomDisplay()
    
    logger.debug('Image fitted to container:', { 
      containerWidth, 
      containerHeight, 
      imageWidth, 
      imageHeight, 
      scale: Math.round(scale * 100) + '%' 
    })
  }
  
  /**
   * Create a new selection from coordinates
   */
  createSelection(coords: SelectionCoords): TextSelection {
    if (!this.currentFile) {
      throw new Error('No current file set for selection creation')
    }
    
    const selection: TextSelection = {
      id: this.generateSelectionId(),
      project_id: this.currentFile.project_id || 'unknown',
      document_id: this.currentFile.id,
      document_type: 'image',
      x: coords.x,
      y: coords.y,
      width: coords.width,
      height: coords.height,
      page_number: 1, // Images are single page
      extracted_text: '',
      ocr_confidence: 0,
      thumbnail_data: 'loading'
    }
    
    this.selections.push(selection)
    this.highlightSelection(selection)
    
    // CRITICAL FIX: Generate thumbnail immediately for image selections
    this.generateThumbnailForSelection(selection)
    
    // CRITICAL FIX: Notify modal about new selection for count updates
    this.notifySelectionCreated(selection)
    
    logger.debug('Image selection created:', selection.id)
    return selection
  }
  
  /**
   * Generate thumbnail for a selection
   */
  public async generateThumbnailForSelection(selection: TextSelection): Promise<void> {
    try {
      logger.debug('Starting thumbnail generation for image selection:', selection.id)
      
      // Generate thumbnail using the existing getThumbnail method
      const thumbnailData = await this.getThumbnail(selection)
      
      // Update the selection with thumbnail data
      const selectionIndex = this.selections.findIndex(s => s.id === selection.id)
      if (selectionIndex !== -1) {
        this.selections[selectionIndex].thumbnail_data = thumbnailData
        logger.debug('Thumbnail generated and updated for selection:', selection.id)
        
        // Update the gallery if it exists
        this.updateGalleryThumbnail(selection.id, thumbnailData)
      }
      
    } catch (error) {
      logger.error('Failed to generate thumbnail for selection:', selection.id, error)
      
      // Mark thumbnail as error
      const selectionIndex = this.selections.findIndex(s => s.id === selection.id)
      if (selectionIndex !== -1) {
        this.selections[selectionIndex].thumbnail_data = 'error'
        this.updateGalleryThumbnail(selection.id, 'error')
      }
    }
  }

  /**
   * Update gallery thumbnail for a selection
   */
  private updateGalleryThumbnail(_selectionId: string, _thumbnailData: string): void {
    try {
      const galleryContainer = document.querySelector('#modal-selections-gallery')
      if (galleryContainer && (galleryContainer as any).selectionsGallery) {
        const gallery = (galleryContainer as any).selectionsGallery
        if (gallery.updateSelections) {
          gallery.updateSelections(this.selections)
        }
      }
    } catch (error) {
      logger.error('Failed to update gallery thumbnail:', error)
    }
  }

  /**
   * Notify modal about new selection for count updates
   */
  private notifySelectionCreated(selection: TextSelection): void {
    try {
      // Dispatch custom event to notify modal about new selection
      const event = new CustomEvent('image-selection-created', {
        detail: { selection, viewer: this }
      })
      window.dispatchEvent(event)
      
      // Also update the gallery if it exists
      const galleryContainer = document.querySelector('#modal-selections-gallery')
      if (galleryContainer && (galleryContainer as any).selectionsGallery) {
        const gallery = (galleryContainer as any).selectionsGallery
        if (gallery.updateSelections) {
          gallery.updateSelections(this.selections)
        }
      }
      
      logger.debug('Notified modal about new image selection:', selection.id)
    } catch (error) {
      logger.error('Failed to notify modal about selection:', error)
    }
  }

  /**
   * Generate thumbnail for a selection
   */
  async getThumbnail(selection: TextSelection): Promise<string> {
    if (!this.imageElement) {
      throw new Error('No image loaded for thumbnail generation')
    }
    
    try {
      // Create a canvas to capture the selection area
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      if (!ctx) {
        throw new Error('Failed to get canvas context')
      }
      
      // Set canvas size to selection size
      canvas.width = selection.width
      canvas.height = selection.height
      
      // Draw the selection area from the image
      ctx.drawImage(
        this.imageElement,
        selection.x, selection.y, selection.width, selection.height,
        0, 0, selection.width, selection.height
      )
      
      // Convert to base64 data URL
      const thumbnailData = canvas.toDataURL('image/png', 0.9)
      
      logger.debug(`Thumbnail generated for selection ${selection.id}`)
      return thumbnailData
      
    } catch (error) {
      logger.error(`Failed to generate thumbnail for selection ${selection.id}:`, error)
      throw error
    }
  }
  
  /**
   * Get all current selections
   */
  getSelections(): TextSelection[] {
    return [...this.selections]
  }
  
  /**
   * Clear all state
   */
  clearState(): void {
    this.selections = []
    this.currentFile = null
    this.scale = 1.0
    this.isSelecting = false
    this.selectionStart = null
    this.currentSelection = null
    
    if (this.selectionOverlay) {
      this.selectionOverlay.innerHTML = ''
    }
    
    logger.debug('ImageViewer state cleared')
  }
  
  /**
   * Destroy the viewer and clean up resources
   */
  destroy(): void {
    this.clearState()
    logger.debug('ImageViewer destroyed')
  }
  
  /**
   * Setup event listeners for image controls
   */
  private setupEventListeners(): void {
    // Zoom in button
    const zoomInBtn = this.container.querySelector('#image-zoom-in')
    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', () => {
        this.zoomIn()
      })
    }
    
    // Zoom out button
    const zoomOutBtn = this.container.querySelector('#image-zoom-out')
    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', () => {
        this.zoomOut()
      })
    }
    
    // Reset zoom button
    const resetBtn = this.container.querySelector('#image-reset-zoom')
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        this.resetZoom()
      })
    }
  }
  
  /**
   * Setup selection event listeners
   */
  private setupSelectionEventListeners(): void {
    if (!this.imageElement || !this.selectionOverlay) return
    
    // Mouse down - start selection
    this.imageElement.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return // Only left mouse button
      
      this.isSelecting = true
      this.selectionStart = {
        x: e.offsetX,
        y: e.offsetY
      }
      
      // Create temporary selection element
      this.currentSelection = document.createElement('div')
      this.currentSelection.className = 'current-selection'
      this.currentSelection.style.position = 'absolute'
      this.currentSelection.style.left = `${this.selectionStart.x}px`
      this.currentSelection.style.top = `${this.selectionStart.y}px`
      this.currentSelection.style.width = '0px'
      this.currentSelection.style.height = '0px'
      this.currentSelection.style.backgroundColor = 'rgba(59, 130, 246, 0.2)'
      this.currentSelection.style.border = '1px dashed rgba(59, 130, 246, 0.8)'
      this.currentSelection.style.pointerEvents = 'none'
      this.currentSelection.style.zIndex = '20'
      
      this.selectionOverlay?.appendChild(this.currentSelection)
      
      e.preventDefault()
    })
    
    // Mouse move - update selection
    this.imageElement.addEventListener('mousemove', (e) => {
      if (!this.isSelecting || !this.selectionStart || !this.currentSelection) return
      
      const currentX = e.offsetX
      const currentY = e.offsetY
      
      const left = Math.min(this.selectionStart.x, currentX)
      const top = Math.min(this.selectionStart.y, currentY)
      const width = Math.abs(currentX - this.selectionStart.x)
      const height = Math.abs(currentY - this.selectionStart.y)
      
      this.currentSelection.style.left = `${left}px`
      this.currentSelection.style.top = `${top}px`
      this.currentSelection.style.width = `${width}px`
      this.currentSelection.style.height = `${height}px`
    })
    
    // Mouse up - finish selection
    this.imageElement.addEventListener('mouseup', (e) => {
      if (!this.isSelecting || !this.selectionStart || !this.currentSelection) return
      
      const currentX = e.offsetX
      const currentY = e.offsetY
      
      const width = Math.abs(currentX - this.selectionStart.x)
      const height = Math.abs(currentY - this.selectionStart.y)
      
      // Only create selection if it's large enough
      if (width > 10 && height > 10) {
        const coords: SelectionCoords = {
          x: Math.min(this.selectionStart.x, currentX),
          y: Math.min(this.selectionStart.y, currentY),
          width: width,
          height: height,
          pageNumber: 1
        }
        
        this.createSelection(coords)
      }
      
      // Clean up
      this.isSelecting = false
      this.selectionStart = null
      if (this.currentSelection) {
        this.currentSelection.remove()
        this.currentSelection = null
      }
    })
  }
  
  /**
   * Zoom in
   */
  private zoomIn(): void {
    this.scale = Math.min(this.scale * 1.2, 5.0)
    this.updateImageScale()
    logger.debug('Zoomed in to:', Math.round(this.scale * 100) + '%')
  }
  
  /**
   * Zoom out
   */
  private zoomOut(): void {
    this.scale = Math.max(this.scale / 1.2, 0.1)
    this.updateImageScale()
    logger.debug('Zoomed out to:', Math.round(this.scale * 100) + '%')
  }
  
  /**
   * Reset zoom to initial fitted scale
   */
  private resetZoom(): void {
    this.scale = this.initialScale
    this.updateImageScale()
    logger.debug('Reset zoom to initial scale:', Math.round(this.scale * 100) + '%')
  }
  
  /**
   * Update image scale
   */
  private updateImageScale(): void {
    if (!this.imageElement) return
    
    const naturalWidth = this.imageElement.naturalWidth
    const naturalHeight = this.imageElement.naturalHeight
    
    this.imageElement.style.width = `${naturalWidth * this.scale}px`
    this.imageElement.style.height = `${naturalHeight * this.scale}px`
    
    this.updateZoomDisplay()
    
    logger.debug('Image scale updated:', {
      scale: Math.round(this.scale * 100) + '%',
      width: Math.round(naturalWidth * this.scale) + 'px',
      height: Math.round(naturalHeight * this.scale) + 'px'
    })
  }
  
  /**
   * Update zoom level display
   */
  private updateZoomDisplay(): void {
    const zoomLevel = this.container.querySelector('#image-zoom-level')
    if (zoomLevel) {
      zoomLevel.textContent = `${Math.round(this.scale * 100)}%`
    }
  }
  
  /**
   * Highlight a selection
   */
  private highlightSelection(selection: TextSelection): void {
    if (!this.selectionOverlay) return
    
    const highlight = document.createElement('div')
    highlight.className = 'selection-highlight'
    highlight.dataset.selectionId = selection.id
    highlight.style.position = 'absolute'
    highlight.style.left = `${selection.x}px`
    highlight.style.top = `${selection.y}px`
    highlight.style.width = `${selection.width}px`
    highlight.style.height = `${selection.height}px`
    highlight.style.backgroundColor = 'rgba(59, 130, 246, 0.3)'
    highlight.style.border = '2px solid rgba(59, 130, 246, 0.8)'
    highlight.style.pointerEvents = 'none'
    highlight.style.zIndex = '10'
    
      this.selectionOverlay?.appendChild(highlight)
  }
  
  /**
   * Generate a unique selection ID
   */
  private generateSelectionId(): string {
    return `image_selection_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Load existing selections for the image document
   */
  async loadExistingSelections(documentId: string): Promise<void> {
    try {
      logger.debug('🖼️ Loading existing selections for image document:', documentId)
      
      // Get current project
      const currentProject = (window as any).appInstance?.appState?.getCurrentProject()
      if (!currentProject) {
        logger.warn('No current project found for loading selections')
        return
      }
      
      // Load selections for this document
      const response = await this.apiService.listProjectSelections(currentProject.id)
      const selections = response.selections || []
      const documentSelections = selections.filter((s: any) => s.document_id === documentId)
      
      logger.debug(`📋 Loaded ${documentSelections.length} selections for image document`)
      
      // Store selections and highlight them
      this.selections = documentSelections
      this.highlightAllSelections()
      
    } catch (error) {
      logger.error('Failed to load existing selections for image document:', error)
    }
  }

  /**
   * Navigate to a specific selection
   */
  async goToSelection(selectionId: string): Promise<void> {
    try {
      logger.debug('🎯 goToSelection called for image selection:', selectionId)
      
      const selection = this.selections.find(s => s.id === selectionId)
      if (!selection) {
        logger.warn('Selection not found:', selectionId)
        return
      }
      
      // Scroll to the selection and highlight it
      this.scrollToSelection(selection)
      this.highlightSelection(selection)
      
      logger.debug('✅ Navigated to image selection:', selectionId)
      
    } catch (error) {
      logger.error('Failed to navigate to image selection:', error)
    }
  }

  /**
   * Initialize selections gallery for image document
   */
  initializeSelectionsGallery(): void {
    try {
      logger.debug('🖼️ Initializing selections gallery for image document')
      
      // Find the gallery container
      const galleryContainer = document.querySelector('#modal-selections-gallery')
      if (!galleryContainer) {
        logger.warn('Gallery container not found for image document')
        return
      }
      
      // Import and create SelectionsGallery
      import('../components/selections-gallery').then(({ SelectionsGallery }) => {
        const gallery = new SelectionsGallery(galleryContainer as HTMLElement, {
          onSelectionNavigate: (id: string) => {
            logger.debug('Image selection navigate callback:', id)
            this.goToSelection(id)
          },
          onRetryThumbnail: (id: string) => {
            logger.debug('Retry thumbnail callback for image selection:', id)
            // Handle thumbnail retry for image selections
          },
          onSelectionReorder: (selections: any[]) => {
            logger.debug('Image selections reordered:', selections.length)
            // Handle reordering if needed
          },
          onSelectionRemove: async (id: string) => {
            logger.debug('🗑️ Removing image selection:', id)
            try {
              await this.apiService.deleteSelection(id)
              // Remove from local selections
              this.selections = this.selections.filter(s => s.id !== id)
              this.highlightAllSelections()
            } catch (error) {
              logger.error('Failed to delete image selection:', error)
            }
          },
          documentType: 'image'
        })
        
        // Store reference to gallery
        ;(galleryContainer as any).selectionsGallery = gallery
        
        // Load selections into gallery
        this.loadSelectionsIntoGallery(gallery)
        
        logger.debug('✅ Image selections gallery initialized')
        
      }).catch(error => {
        logger.error('Failed to initialize image selections gallery:', error)
      })
      
    } catch (error) {
      logger.error('Failed to initialize image selections gallery:', error)
    }
  }

  /**
   * Scroll to a specific selection
   */
  private scrollToSelection(selection: TextSelection): void {
    if (!this.selectionOverlay) return
    
    // Find the selection highlight element
    const highlight = this.selectionOverlay.querySelector(`[data-selection-id="${selection.id}"]`) as HTMLElement
    if (highlight) {
      highlight.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  /**
   * Highlight all current selections
   */
  private highlightAllSelections(): void {
    if (!this.selectionOverlay) return
    
    // Clear existing highlights
    this.selectionOverlay.innerHTML = ''
    
    // Highlight all selections
    this.selections.forEach(selection => {
      this.highlightSelection(selection)
    })
  }

  /**
   * Load selections into gallery
   */
  private async loadSelectionsIntoGallery(gallery: any): Promise<void> {
    try {
      if (!this.currentFile) return
      
      // Get current project
      const currentProject = (window as any).appInstance?.appState?.getCurrentProject()
      if (!currentProject) return
      
      // Load selections for this document
      const response = await this.apiService.listProjectSelections(currentProject.id)
      const selections = response.selections || []
      const documentSelections = selections.filter((s: any) => s.document_id === this.currentFile.id)
      
      logger.debug(`📋 Loading ${documentSelections.length} selections into image gallery`)
      
      // Update gallery with selections
      gallery.updateSelections(documentSelections)
      
    } catch (error) {
      logger.error('Failed to load selections into image gallery:', error)
    }
  }
}
